import { Node, mergeAttributes, InputRule, PasteRule } from '@tiptap/vue-3';
import { PluginKey } from '@tiptap/pm/state';
import { default as Suggestion } from '@tiptap/suggestion';
import {
	inputEmojiRegex,
	isEmojiSupported,
	pasteEmojiRegex,
	shortcodeToEmoji,
} from './helpers';
import { ALL_EMOJIS } from './constants';
import type {
	EmojiItem,
	EmojiOptions,
	SupportCache,
	EmojiStorage,
} from './types';
import { doubleClickSelectPlugin } from '@/features/App/AppTextEditor/plugins/DoubleClickSelectPlugin';

const EmojiSuggestionPluginKey = new PluginKey('emojiSuggestion');

export const Emoji = Node.create<EmojiOptions, EmojiStorage>({
	name: 'emoji',
	inline: true,
	group: 'inline',
	selectable: false,

	addOptions() {
		return {
			HTMLAttributes: {},
			emojis: ALL_EMOJIS,
			forceFallbackImages: false,
			suggestion: {
				char: ':',
				pluginKey: EmojiSuggestionPluginKey,
				command: ({ editor, range, props }) => {
					const nextNode = editor.view.state.selection.$to.nodeAfter;

					if (nextNode?.text?.startsWith(' ')) {
						range.to += 1;
					}

					editor
						.chain()
						.focus()
						.insertContentAt(range, [
							{ type: 'emoji', attrs: props },
							{ type: 'text', text: ' ' },
						])
						.run();
				},
				allow: ({ state, range }) => {
					const $pos = state.doc.resolve(range.from);
					return !!$pos.parent.type.contentMatch.matchType(
						state.schema.nodes.emoji
					);
				},
			},
		};
	},

	addStorage() {
		const { emojis } = this.options as EmojiOptions;
		const supportCache: SupportCache = emojis.reduce(
			(acc: SupportCache, emoji: EmojiItem) => {
				if (emoji?.name && !acc[emoji?.name]) {
					acc[emoji.name] = isEmojiSupported(emoji.emoji);
				}
				return acc;
			},
			{}
		);

		return {
			emojis,
			isSupported: (emoji: EmojiItem) =>
				Boolean(emoji.name && supportCache[emoji.name]),
		};
	},

	addAttributes() {
		return {
			name: {
				default: null,
				parseHTML: (element: HTMLElement) => element.dataset.name,
				renderHTML: (attributes) => ({ 'data-name': attributes.name }),
			},
		};
	},

	parseHTML() {
		return [{ tag: 'span[data-type="emoji"]' }];
	},

	renderHTML({ HTMLAttributes, node }) {
		const emojiData = shortcodeToEmoji(
			node.attrs.name,
			this.options.emojis
		);
		const attributes = mergeAttributes(
			HTMLAttributes,
			this.options.HTMLAttributes,
			{ 'data-type': 'emoji' }
		);

		if (!emojiData) {
			return ['span', attributes, `:${node.attrs.name}:`];
		}

		const isSupported = this.storage.isSupported(emojiData);
		const shouldUseFallback =
			this.options.forceFallbackImages ||
			(!isSupported && emojiData.fallbackImage);

		return [
			'span',
			attributes,
			shouldUseFallback
				? [
						'img',
						{
							src: emojiData.fallbackImage,
							draggable: 'false',
							loading: 'lazy',
							align: 'absmiddle',
						},
					]
				: emojiData.emoji || `:${emojiData.shortcodes[0]}:`,
		];
	},

	addCommands() {
		return {
			setEmoji:
				(name) =>
				({ chain }) => {
					const emojiData = shortcodeToEmoji(
						name,
						this.options.emojis
					);

					if (!emojiData) {
						return false;
					}

					return chain()
						.insertContent({
							type: 'emoji',
							attrs: { name: emojiData.name },
						})
						.run();
				},
		};
	},

	addInputRules() {
		return [
			new InputRule({
				find: inputEmojiRegex,
				handler: ({ range, match, chain }) => {
					const shortcode = match[1];
					const emoji = shortcodeToEmoji(
						shortcode,
						this.options.emojis
					);

					if (emoji) {
						chain()
							.insertContentAt(range, {
								type: 'emoji',
								attrs: { name: shortcode },
							})
							.run();
					}
				},
			}),
		];
	},

	addPasteRules() {
		return [
			new PasteRule({
				find: pasteEmojiRegex,
				handler: ({ range, match, chain }) => {
					const shortcode = match[1];
					const emoji = shortcodeToEmoji(
						shortcode,
						this.options.emojis
					);

					if (emoji) {
						chain()
							.insertContentAt(
								range,
								{ type: 'emoji', attrs: { name: shortcode } },
								{ updateSelection: false }
							)
							.run();
					}
				},
			}),
		];
	},

	addProseMirrorPlugins() {
		return [
			Suggestion({ editor: this.editor, ...this.options.suggestion }),
			doubleClickSelectPlugin.call(this, new PluginKey('emoji')),
		];
	},
});
